/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './GraphicMessage';

const GraphicMessageManager: ComponentManagerFn<'GraphicMessage'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'GraphicMessage'>(platform, 'GraphicMessage'),
    type: 'GraphicMessage',
    name: 'Graphic Message',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      message: {
        label: 'Message',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      graphicType: {
        label: 'Graphic Type',
        type: 'select',
        options: toOptions(['success', 'warning', 'info']),
        required: true,
      },
    },
    getDisplayText: (item) => item.props.graphicType,
    category: 'Others',
    icon: { iconName: 'comments-alt' },
    description: `**Overview**<br/>
The Graphic Message is a feedback component used to display a placeholder illustration accompanied by a text label. It is designed to provide visual context for empty states, system statuses, or user feedback in a more engaging way than plain text.
Use this component to handle "null" states gracefully, such as when a search returns no results or a list has no items.

**1. Configuration**<br/>
* **Message (Required):** The text string to display below the graphic (e.g., "No records found" or "Submission Successful"). This property supports expressions, allowing you to generate the message dynamically based on application logic or variables.
* **Graphic Type (Required):** Determines the visual theme and the specific SVG illustration rendered:
    * **Info:** Used for general notifications, neutral empty states, or helpful tips.
    * **Success:** Used for positive confirmations, such as after successfully saving a form or completing a task.
    * **Warning:** Used for alerts, missing requirements, or highlighting errors that require user attention.

**2. Interactivity**<br/>
* **TriggerOnClick:** The component allows a **TriggerOnClick** child component. This enables the entire graphic and message area to act as a functional button.
* **Example:** An empty state message saying "No Items Found" can be clicked by the user to trigger an "Add New Item" workflow or open a creation modal.

**3. Best Use Cases**<br/>
* **Empty Data Grids:** Displaying a "No Data Available" graphic when a database query returns zero rows, making the UI feel intentional rather than broken.
* **Search Interfaces:** Informing the user when their specific search criteria yielded no matches and suggesting they try different keywords.
* **Success Screens:** Providing a clear, visually pleasing confirmation that a long-running process or multi-step form has completed successfully.
* **Dashboard Widgets:** Filling the space of a chart or list that currently has no data to display, maintaining the structure of the dashboard layout.`,
  };
};

export default GraphicMessageManager;
